import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Building, FileCheck, AlertTriangle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../auth/AuthProvider';

type FormData = {
  organizationName: string;
  organizationType: '501c3' | 'nonprofit' | 'other';
  taxId: string;
  organizationAge: string;
  stateRegistration: string;
  agreeToTerms: boolean;
};

const OrganizationOnboarding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<FormData>({
    organizationName: '',
    organizationType: '501c3',
    taxId: '',
    organizationAge: '',
    stateRegistration: '',
    agreeToTerms: false,
  });

  // Check if user has already completed onboarding
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!user) return;

      try {
        const { data: orgs, error: orgError } = await supabase
          .from('organizations')
          .select('*')
          .eq('owner_id', user.id)
          .limit(1);

        if (orgError) throw orgError;

        // If user has an organization, redirect to campaigns
        if (orgs && orgs.length > 0) {
          navigate('/campaigns');
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
      }
    };

    checkOnboardingStatus();
  }, [user, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const validateForm = () => {
    // Validate organization type
    if (formData.organizationType === 'other') {
      setError('Only 501(c)(3) and nonprofit organizations are eligible to conduct raffles.');
      return false;
    }

    // Validate organization age (minimum 1 year)
    if (parseInt(formData.organizationAge) < 1) {
      setError('Organization must be at least 1 year old to conduct raffles.');
      return false;
    }

    // Validate tax ID format
    const taxIdPattern = /^\d{2}-\d{7}$/;
    if (!taxIdPattern.test(formData.taxId)) {
      setError('Please enter a valid tax ID in XX-XXXXXXX format.');
      return false;
    }

    // Validate terms acceptance
    if (!formData.agreeToTerms) {
      setError('You must agree to the terms and conditions to continue.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      if (!user) {
        throw new Error('User not authenticated');
      }

      // Create organization
      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .insert([
          {
            name: formData.organizationName,
            owner_id: user.id,
            organization_type: formData.organizationType,
            tax_id: formData.taxId,
            organization_age: parseInt(formData.organizationAge),
            state_registration: formData.stateRegistration
          }
        ])
        .select()
        .single();

      if (orgError) throw orgError;

      // Redirect to campaigns page
      navigate('/campaigns');
    } catch (error: any) {
      console.error('Error creating organization:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-purple-600 p-8 text-white">
          <div className="flex items-center justify-center mb-4">
            <Building className="h-12 w-12" />
          </div>
          <h1 className="text-3xl font-bold text-center">Organization Setup</h1>
          <p className="text-white/80 text-center mt-2">
            Complete your organization profile to start creating raffles
          </p>
        </div>

        <div className="p-8">
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg mb-8">
            <div className="flex">
              <Shield className="h-5 w-5 text-blue-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">Legal Requirements</h3>
                <p className="text-sm text-blue-700 mt-1">
                  To comply with state laws, only registered 501(c)(3) and nonprofit organizations 
                  that have been operating for at least 1 year may conduct raffles. Please ensure 
                  you have proper registration in your state.
                </p>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 text-red-800 p-4 rounded-md mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Organization Name
              </label>
              <input
                type="text"
                name="organizationName"
                value={formData.organizationName}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organization Type
                </label>
                <select
                  name="organizationType"
                  value={formData.organizationType}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                >
                  <option value="501c3">501(c)(3) Organization</option>
                  <option value="nonprofit">Other Nonprofit Organization</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tax ID (EIN)
                </label>
                <input
                  type="text"
                  name="taxId"
                  value={formData.taxId}
                  onChange={handleChange}
                  placeholder="XX-XXXXXXX"
                  className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Years in Operation
                </label>
                <input
                  type="number"
                  name="organizationAge"
                  value={formData.organizationAge}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State Raffle Registration Number
                </label>
                <input
                  type="text"
                  name="stateRegistration"
                  value={formData.stateRegistration}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-yellow-400" />
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    By creating an organization, you confirm that you meet all state and federal requirements 
                    for conducting raffles, including proper registration and reporting obligations. You are 
                    responsible for ensuring compliance with all applicable laws and regulations.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleChange}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  required
                />
              </div>
              <div className="ml-3">
                <label className="text-sm text-gray-700">
                  I have read and agree to the{' '}
                  <a
                    href="/terms"
                    target="_blank"
                    className="text-purple-600 hover:text-purple-800"
                  >
                    Terms and Conditions
                  </a>
                  . I confirm that my organization is eligible to conduct raffles and agrees to comply 
                  with all applicable state and federal regulations.
                </label>
              </div>
            </div>

            <div className="flex justify-end pt-6">
              <button
                type="submit"
                disabled={loading || !formData.agreeToTerms}
                className={`flex items-center px-6 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors ${
                  (loading || !formData.agreeToTerms) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {loading ? (
                  'Creating Organization...'
                ) : (
                  <>
                    <FileCheck className="h-5 w-5 mr-2" />
                    Complete Setup
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OrganizationOnboarding;